import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useStock } from "@/context/StockContext";
import { ProductSubcategory, SubcategoryFormData } from "@/types/stock";
import { Tag, Palette } from "lucide-react";

interface SubcategoryFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  subcategory?: ProductSubcategory | null;
  onSubmit: (data: SubcategoryFormData) => Promise<void>;
}

const SubcategoryFormDialog: React.FC<SubcategoryFormDialogProps> = ({
  isOpen,
  onClose,
  subcategory,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { categories } = useStock();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState<SubcategoryFormData>({
    name: "",
    description: "",
    categoryId: "",
    icon: "",
    color: "#3B82F6",
    isActive: true,
  });

  // Predefined colors for quick selection
  const predefinedColors = [
    "#3B82F6", // Blue
    "#EF4444", // Red
    "#10B981", // Green
    "#F59E0B", // Yellow
    "#8B5CF6", // Purple
    "#F97316", // Orange
    "#06B6D4", // Cyan
    "#84CC16", // Lime
    "#EC4899", // Pink
    "#6B7280", // Gray
  ];

  // Common icons for subcategories
  const commonIcons = [
    "📱",
    "💻",
    "⌚",
    "🎧",
    "📷",
    "🖥️",
    "⌨️",
    "🖱️",
    "🔌",
    "🔋",
    "📺",
    "🎮",
    "🎵",
    "📞",
    "💾",
    "🖨️",
    "📡",
    "🔊",
    "🎤",
    "📹",
  ];

  useEffect(() => {
    if (subcategory) {
      setFormData({
        name: subcategory.name,
        description: subcategory.description || "",
        categoryId: subcategory.categoryId,
        icon: subcategory.icon || "",
        color: subcategory.color || "#3B82F6",
        isActive: subcategory.isActive,
      });
    } else {
      setFormData({
        name: "",
        description: "",
        categoryId: "",
        icon: "",
        color: "#3B82F6",
        isActive: true,
      });
    }
  }, [subcategory, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.categoryId) {
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    field: keyof SubcategoryFormData,
    value: string | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            {subcategory
              ? t("stock.subcategory.edit")
              : t("stock.subcategory.add")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Category Selection */}
          <div className="space-y-2">
            <Label htmlFor="categoryId">{t("stock.category.name")} *</Label>
            <Select
              value={formData.categoryId}
              onValueChange={(value) => handleInputChange("categoryId", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("stock.category.selectCategory")} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    <div className="flex items-center gap-2">
                      {category.icon && <span>{category.icon}</span>}
                      <span>{category.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Subcategory Name */}
          <div className="space-y-2">
            <Label htmlFor="name">{t("stock.subcategory.name")} *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder={t("stock.subcategory.namePlaceholder")}
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">
              {t("stock.subcategory.description")}
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder={t("stock.subcategory.descriptionPlaceholder")}
              rows={3}
            />
          </div>

          {/* Icon Selection */}
          <div className="space-y-2">
            <Label>{t("stock.subcategory.icon")}</Label>
            <div className="space-y-2">
              <Input
                value={formData.icon}
                onChange={(e) => handleInputChange("icon", e.target.value)}
                placeholder={t("stock.subcategory.iconPlaceholder")}
              />
              <div className="grid grid-cols-10 gap-1">
                {commonIcons.map((icon) => (
                  <Button
                    key={icon}
                    type="button"
                    variant={formData.icon === icon ? "default" : "outline"}
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => handleInputChange("icon", icon)}
                  >
                    {icon}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Color Selection */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Palette className="h-4 w-4" />
              {t("stock.subcategory.color")}
            </Label>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  value={formData.color}
                  onChange={(e) => handleInputChange("color", e.target.value)}
                  className="w-12 h-8 p-1 border rounded"
                />
                <Input
                  value={formData.color}
                  onChange={(e) => handleInputChange("color", e.target.value)}
                  placeholder="#3B82F6"
                  className="flex-1"
                />
              </div>
              <div className="grid grid-cols-10 gap-1">
                {predefinedColors.map((color) => (
                  <Button
                    key={color}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0 border-2"
                    style={{
                      backgroundColor: color,
                      borderColor:
                        formData.color === color ? "#000" : "#e5e7eb",
                    }}
                    onClick={() => handleInputChange("color", color)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Active Status */}
          <div className="flex items-center justify-between">
            <Label htmlFor="isActive">{t("common.active")}</Label>
            <Switch
              id="isActive"
              checked={formData.isActive}
              onCheckedChange={(checked) =>
                handleInputChange("isActive", checked)
              }
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={
                loading || !formData.name.trim() || !formData.categoryId
              }
            >
              {loading ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SubcategoryFormDialog;
